'use client';

import { useMemo, useState } from 'react';
import {
  buildShareButtonsSnippet,
  buildShareUrl,
  shareNetworkLabels,
  type ShareNetwork,
} from '@/lib/browser-widgetgen-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const ALL_NETWORKS: ShareNetwork[] = [
  'twitter',
  'facebook',
  'linkedin',
  'whatsapp',
  'reddit',
  'email',
];

const NETWORK_COLORS: Record<ShareNetwork, string> = {
  twitter: '#000000',
  facebook: '#1877f2',
  linkedin: '#0a66c2',
  whatsapp: '#25d366',
  reddit: '#ff4500',
  email: '#6b7280',
};

export default function BrowserSocialShareGenerator() {
  const [url, setUrl] = useState('https://example.com/my-article');
  const [title, setTitle] = useState('Check out this article');
  const [networks, setNetworks] = useState<ShareNetwork[]>(ALL_NETWORKS);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const snippet = useMemo(
    () => buildShareButtonsSnippet({ url, title, networks }),
    [url, title, networks],
  );

  const rawUrls = useMemo(
    () =>
      networks.map((n) => `${shareNetworkLabels[n]}: ${buildShareUrl(n, url, title)}`).join('\n'),
    [networks, url, title],
  );

  function toggleNetwork(network: ShareNetwork) {
    setNetworks((prev) =>
      prev.includes(network)
        ? prev.filter((n) => n !== network)
        : ALL_NETWORKS.filter((n) => n === network || prev.includes(n)),
    );
  }

  async function handleCopy(value: string, label: string) {
    try {
      await copyText(value);
      setCopyMessage(`${label} copied to clipboard.`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Client-side</span>
        <h2 className="tool-live-title">Social share button generator</h2>
        <p className="tool-live-subtitle">
          Enter a URL and title to generate share buttons and raw share links for X, Facebook,
          LinkedIn, WhatsApp, Reddit, and email. Built in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Page URL</span>
              <input onChange={(e) => setUrl(e.target.value)} type="text" value={url} />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Share title</span>
              <input onChange={(e) => setTitle(e.target.value)} type="text" value={title} />
            </label>
            <div className="tool-live-field tool-live-field-full">
              <span>Networks</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
                {ALL_NETWORKS.map((network) => (
                  <label
                    key={network}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                  >
                    <input
                      checked={networks.includes(network)}
                      onChange={() => toggleNetwork(network)}
                      type="checkbox"
                    />
                    <span>{shareNetworkLabels[network]}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => handleCopy(snippet, 'HTML snippet')}
              type="button"
            >
              Copy HTML
            </button>
            <button
              className="button button-secondary"
              onClick={() => handleCopy(rawUrls, 'Share URLs')}
              type="button"
            >
              Copy raw URLs
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              padding: 16,
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
              background: '#ffffff',
            }}
          >
            {networks.map((network) => (
              <a
                href={buildShareUrl(network, url, title)}
                key={network}
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  background: NETWORK_COLORS[network],
                  color: '#ffffff',
                  textDecoration: 'none',
                  borderRadius: 6,
                  padding: '8px 14px',
                  fontSize: 14,
                }}
                target="_blank"
              >
                Share on {shareNetworkLabels[network]}
              </a>
            ))}
            {networks.length === 0 ? (
              <span style={{ color: '#6b7280', fontSize: 14 }}>Select at least one network.</span>
            ) : null}
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>HTML snippet</span>
            <textarea readOnly rows={8} value={snippet} />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>Raw share URLs</span>
            <textarea readOnly rows={6} value={rawUrls} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
